import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, TrendingUp, Bookmark, Clock, Flame, Star, Zap } from 'lucide-react';
import { getDossiers, getPopularDossiers, getLatestDossiers, getEditions, getInfluenceIndex, getFeaturedDossier } from '../services/api';
import { formatDateESTShort, formatRelativeTime } from '../utils/dateUtils';

// Forbes-style typography
const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

// Placeholder images for articles without cover
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800',
  'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800',
  'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800',
  'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800',
];

const getImageUrl = (item, index = 0) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
};

// ============================================
// MOBILE ARTICLE CARD - Fitts Law optimized (large touch targets)
// ============================================
const MobileArticleCard = ({ article, index, variant = 'default' }) => {
  const imageUrl = getImageUrl(article, index);
  
  if (variant === 'compact') {
    return (
      <Link 
        to={`/dossiers/${article.dossier_id}`}
        className="flex gap-3 py-3 min-h-[72px] active:bg-gray-50 transition-colors"
        data-testid={`article-card-${article.dossier_id}`}
      >
        <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
          <img 
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 py-1">
          <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider mb-1">
            {article.sector || 'ACTUALIDAD'}
            {article.primary_country && article.primary_country !== 'Global' && (
              <span className="ml-1 text-gray-500">• {article.primary_country}</span>
            )}
          </p>
          <h3 
            className="text-sm font-bold text-gray-900 leading-snug line-clamp-2"
            style={{ fontFamily: SERIF_FONT }}
          >
            {article.title}
          </h3>
        </div>
      </Link>
    );
  }
  
  return (
    <Link 
      to={`/dossiers/${article.dossier_id}`}
      className="block active:opacity-90 transition-opacity"
      data-testid={`article-card-${article.dossier_id}`}
    >
      <div className="relative aspect-[16/10] mb-3 overflow-hidden bg-gray-100 rounded-lg">
        <img 
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider mb-1">
        {article.sector || 'ACTUALIDAD'}
        {article.primary_country && article.primary_country !== 'Global' && (
          <span className="ml-1 text-gray-500">• {article.primary_country}</span>
        )}
      </p>
      <h3 
        className="text-base font-bold text-gray-900 leading-snug line-clamp-2 mb-1"
        style={{ fontFamily: SERIF_FONT }}
      >
        {article.title}
      </h3>
      <p className="text-xs text-gray-500 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {article.author || 'Equipo Editorial'}
      </p>
    </Link>
  );
};

// ============================================
// POPULAR / LO ÚLTIMO TABS COMPONENT
// ============================================
const PopularLatestTabs = ({ popularArticles, latestArticles }) => {
  const [activeTab, setActiveTab] = useState('popular');
  
  const articles = activeTab === 'popular' ? popularArticles : latestArticles;
  
  return (
    <>
      <div className="flex gap-6 mb-4">
        <button 
          onClick={() => setActiveTab('popular')}
          className={`text-sm font-bold pb-2 transition-colors ${
            activeTab === 'popular' 
              ? 'text-gray-900 border-b-2 border-red-600' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Flame className="w-4 h-4 inline mr-1" />
          Popular
        </button>
        <button 
          onClick={() => setActiveTab('latest')}
          className={`text-sm font-bold pb-2 transition-colors ${
            activeTab === 'latest' 
              ? 'text-gray-900 border-b-2 border-red-600' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Lo Último
        </button>
      </div>
      <div className="space-y-4">
        {articles.slice(0, 5).map((d, i) => (
          <Link 
            key={d.dossier_id}
            to={`/dossiers/${d.dossier_id}`}
            className="flex gap-3 group"
          >
            <span className="text-2xl font-bold text-gray-300 group-hover:text-red-600 transition-colors">
              {i + 1}.
            </span>
            <div className="flex-1">
              <p className="text-sm text-gray-800 group-hover:text-red-600 transition-colors leading-snug line-clamp-2">
                {d.title}
              </p>
              {activeTab === 'latest' && d.created_at && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatDateESTShort(d.published_at || d.created_at)}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </>
  );
};

// ============================================
// FORBES-STYLE HOMEPAGE - Optimized for Mobile UX Laws
// ============================================
const HomePage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [influenceIndex, setInfluenceIndex] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendingIndex, setTrendingIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('popular'); // For mobile tabs

  const [popularDossiers, setPopularDossiers] = useState([]);
  const [latestDossiers, setLatestDossiers] = useState([]);
  const [featuredDossier, setFeaturedDossier] = useState(null);
  const [featuredMethod, setFeaturedMethod] = useState('algorithm');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dossiersData, popularData, latestData, editionsData, influenceData, featuredData] = await Promise.all([
          getDossiers(),
          getPopularDossiers(),
          getLatestDossiers(),
          getEditions(),
          getInfluenceIndex(),
          getFeaturedDossier()
        ]);
        setDossiers(dossiersData || []);
        setPopularDossiers(popularData || []);
        setLatestDossiers(latestData || []);
        setEditions(editionsData || []);
        setInfluenceIndex(influenceData || []);
        if (featuredData?.dossier) {
          setFeaturedDossier(featuredData.dossier);
          setFeaturedMethod(featuredData.selection_method);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-scroll trending bar
  useEffect(() => {
    const interval = setInterval(() => {
      setTrendingIndex(prev => (prev + 1) % Math.max(dossiers.length - 3, 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [dossiers.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  // Use featured dossier if available, otherwise fall back to popular
  const coverStory = featuredDossier || popularDossiers[0] || dossiers[0];
  // Filter out the cover story from side articles
  const sideArticles = popularDossiers.filter(d => d.dossier_id !== coverStory?.dossier_id).slice(0, 2);
  // Miller's Law: 5 items in popular list
  const popularArticles = popularDossiers.slice(0, 5);
  const latestArticles = latestDossiers.slice(0, 5);
  const moreArticles = dossiers.slice(5, 13);

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      {/* TRENDING BAR - Mobile optimized */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center gap-3">
            <span className="text-red-600 font-bold text-[10px] uppercase tracking-wider flex items-center gap-1 flex-shrink-0">
              <TrendingUp className="w-3 h-3" />
              <span className="hidden sm:inline">TRENDING</span>
            </span>
            <div className="flex-1 overflow-hidden">
              <div 
                className="flex gap-4 lg:gap-6 transition-transform duration-500" 
                style={{ transform: `translateX(-${trendingIndex * 25}%)` }}
              >
                {dossiers.slice(0, 8).map((d) => (
                  <Link 
                    key={d.dossier_id} 
                    to={`/dossiers/${d.dossier_id}`}
                    className="text-xs lg:text-sm text-gray-700 hover:text-red-600 whitespace-nowrap transition-colors py-1"
                  >
                    {d.title?.substring(0, 40)}...
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EDITION SELECTOR - Desktop only */}
      <div className="hidden lg:block max-w-7xl mx-auto px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">Edición Latinoamérica</span>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="max-w-7xl mx-auto px-4 py-4 lg:py-8">
        
        {/* MOBILE: Cover Story First (most important, largest target) */}
        <div className="lg:hidden mb-6">
          {coverStory && (
            <Link 
              to={`/dossiers/${coverStory.dossier_id}`} 
              className="block active:opacity-90 transition-opacity"
              data-testid="cover-story"
            >
              {/* Large Image - Fitts Law */}
              <div className="relative aspect-[16/9] mb-4 overflow-hidden bg-gray-100 rounded-xl">
                <img 
                  src={getImageUrl(coverStory, 0)}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {/* Category Badge */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                    {coverStory.sector || 'PORTADA'}
                    {coverStory.primary_country && coverStory.primary_country !== 'Global' && (
                      <span className="ml-1 opacity-80">• {coverStory.primary_country}</span>
                    )}
                  </span>
                  {coverStory.is_breaking && (
                    <span className="bg-yellow-500 text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      ÚLTIMA HORA
                    </span>
                  )}
                </div>
                {coverStory.is_featured && (
                  <div className="absolute top-3 right-3">
                    <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                  </div>
                )}
              </div>
              
              {/* Title - Large readable text */}
              <h1 
                className="text-xl font-bold text-gray-900 leading-tight mb-2"
                style={{ fontFamily: SERIF_FONT }}
              >
                {coverStory.title}
              </h1>
              
              {/* Description */}
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-2">
                {coverStory.what_changed_today || coverStory.executive_summary?.[0] || ''}
              </p>
              
              {/* Meta */}
              <p className="text-xs text-gray-500 flex items-center gap-2">
                <Clock className="w-3 h-3" />
                {coverStory.author || 'Equipo Editorial'}
              </p>
            </Link>
          )}
        </div>

        {/* MOBILE: Quick Section Tabs - Hick's Law (2 clear options) */}
        <div className="lg:hidden mb-4">
          <div className="flex gap-2 border-b border-gray-200">
            {/* Miller's Law: Only 2 tabs */}
            <button
              onClick={() => setActiveTab('popular')}
              className={`flex-1 py-3 text-sm font-semibold transition-colors min-h-[48px] ${
                activeTab === 'popular'
                  ? 'text-gray-900 border-b-2 border-red-600'
                  : 'text-gray-500'
              }`}
            >
              Popular
            </button>
            <button
              onClick={() => setActiveTab('latest')}
              className={`flex-1 py-3 text-sm font-semibold transition-colors min-h-[48px] ${
                activeTab === 'latest'
                  ? 'text-gray-900 border-b-2 border-red-600'
                  : 'text-gray-500'
              }`}
            >
              Últimas
            </button>
          </div>
        </div>

        {/* MOBILE: Article List - Miller's Law (5 items) */}
        <div className="lg:hidden">
          <div className="divide-y divide-gray-100">
            {(activeTab === 'popular' ? popularArticles : latestArticles).slice(0, 5).map((article, i) => (
              <MobileArticleCard key={article.dossier_id} article={article} index={i} variant="compact" />
            ))}
          </div>
          
          {/* View More - Large touch target */}
          <Link
            to="/dossiers"
            className="flex items-center justify-center gap-2 w-full py-4 mt-4 bg-gray-100 rounded-xl text-sm font-semibold text-gray-700 active:bg-gray-200 transition-colors min-h-[56px]"
          >
            Ver todas las noticias
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* DESKTOP: 3-Column Layout */}
        <div className="hidden lg:grid grid-cols-12 gap-8">
          
          {/* LEFT COLUMN - Morning Brief + Popular */}
          <div className="col-span-3">
            {/* Morning Brief */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: SERIF_FONT }}>
                Resumen del Día
              </h2>
              <div className="space-y-4">
                {dossiers.slice(0, 3).map((d) => (
                  <Link 
                    key={d.dossier_id}
                    to={`/dossiers/${d.dossier_id}`}
                    className="block group"
                  >
                    <div className="flex gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <p className="text-sm text-gray-700 group-hover:text-red-600 transition-colors leading-snug">
                        {d.title}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Generado por IA. <Link to="/about" className="text-blue-600 hover:underline">Más información.</Link>
              </p>
            </div>

            {/* Popular / Lo Último Tabs */}
            <div className="border-t border-gray-200 pt-6">
              <PopularLatestTabs 
                popularArticles={popularArticles}
                latestArticles={latestArticles}
              />
            </div>
          </div>

          {/* CENTER COLUMN - Cover Story */}
          <div className="col-span-5">
            {coverStory && (
              <Link to={`/dossiers/${coverStory.dossier_id}`} className="block group">
                {/* Cover Image */}
                <div className="relative aspect-[4/3] mb-4 overflow-hidden bg-gray-100">
                  <img 
                    src={getImageUrl(coverStory, 0)}
                    alt={coverStory.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Breaking News Badge */}
                  {coverStory.is_breaking && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-yellow-500 text-black text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded flex items-center gap-1">
                        <Zap className="w-4 h-4" />
                        ÚLTIMA HORA
                      </span>
                    </div>
                  )}
                  {/* Featured Star */}
                  {coverStory.is_featured && (
                    <div className="absolute top-4 right-4">
                      <Star className="w-6 h-6 text-yellow-400 drop-shadow-lg" fill="currentColor" />
                    </div>
                  )}
                </div>
                
                {/* Category Tag */}
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {coverStory.sector || 'PORTADA DEL DÍA'}
                </p>
                
                {/* Title */}
                <h1 
                  className="text-3xl lg:text-4xl font-bold text-gray-900 group-hover:text-red-600 transition-colors leading-tight mb-4"
                  style={{ fontFamily: SERIF_FONT }}
                >
                  {coverStory.title}
                </h1>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                  {coverStory.what_changed_today || coverStory.executive_summary?.[0] || ''}
                </p>
                
                {/* Author */}
                <p className="text-sm text-gray-500">
                  Por <span className="text-gray-900 hover:text-red-600">{coverStory.author || 'Equipo Editorial'}</span>, 
                  <span className="text-gray-400"> Influencia & Poder</span>
                </p>
              </Link>
            )}
          </div>

          {/* RIGHT COLUMN - Side Articles */}
          <div className="col-span-4 space-y-6">
            {sideArticles.map((article, i) => (
              <Link 
                key={article.dossier_id}
                to={`/dossiers/${article.dossier_id}`}
                className="block group"
              >
                {/* Image */}
                <div className="relative aspect-[16/10] mb-3 overflow-hidden bg-gray-100">
                  <img 
                    src={getImageUrl(article, i + 1)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                {/* Category */}
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {article.sector || 'ACTUALIDAD'}
                </p>
                
                {/* Title */}
                <h3 
                  className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors leading-snug mb-2 line-clamp-3"
                  style={{ fontFamily: SERIF_FONT }}
                >
                  {article.title}
                </h3>
                
                {/* Author */}
                <p className="text-xs text-gray-500">
                  Por <span className="text-gray-700">{article.author || 'Equipo Editorial'}</span>, Influencia & Poder
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* MOBILE: Influence Index Preview - Miller's Law (top 3 only) */}
        {influenceIndex.length > 0 && (
          <section className="lg:hidden mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
                Índice de Influencia
              </h2>
              <Link to="/influence-index" className="text-xs text-red-600 font-semibold">
                Ver más →
              </Link>
            </div>
            {/* Only 3 items on mobile - Miller's Law */}
            <div className="space-y-2">
              {influenceIndex.slice(0, 3).map((entry, i) => (
                <div 
                  key={entry.entry_id} 
                  className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                >
                  <span className="text-xl font-bold text-red-600 w-8">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{entry.entity_name}</p>
                    <p className="text-xs text-gray-500">{entry.entity_type}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    entry.movement > 0 ? 'bg-green-100 text-green-700' : 
                    entry.movement < 0 ? 'bg-red-100 text-red-700' : 
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {entry.movement > 0 ? `+${entry.movement}` : entry.movement}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* DESKTOP: Índice de Influencia */}
        {influenceIndex.length > 0 && (
          <section className="hidden lg:block mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
                <Link to="/influence-index" className="hover:text-red-600 transition-colors">
                  Índice de Influencia
                </Link>
              </h2>
              <Link to="/influence-index" className="text-sm text-gray-500 hover:text-red-600 transition-colors">
                Ver ranking completo →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {influenceIndex.slice(0, 5).map((entry, i) => (
                <div key={entry.entry_id} className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl font-bold text-red-600">#{i + 1}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${entry.movement > 0 ? 'bg-green-100 text-green-700' : entry.movement < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {entry.movement > 0 ? `+${entry.movement}` : entry.movement}
                    </span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm">{entry.entity_name}</p>
                  <p className="text-xs text-gray-500">{entry.entity_type}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* MOBILE: Edition CTA - Fitts Law (large button) */}
        {editions.length > 0 && (
          <section className="lg:hidden mt-8 pt-6 border-t border-gray-200">
            <Link 
              to={`/edition/${editions[0].edition_id}`}
              className="block bg-gray-900 rounded-xl p-4 active:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* Thumbnail */}
                <div className="w-16 h-20 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">S{editions[0].week_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-1">Edición Semanal</p>
                  <h3 
                    className="text-white font-bold text-sm leading-tight line-clamp-2"
                    style={{ fontFamily: SERIF_FONT }}
                  >
                    {editions[0].title}
                  </h3>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
              </div>
            </Link>
          </section>
        )}

        {/* DESKTOP: La Edición */}
        {editions.length > 0 && (
          <section className="hidden lg:block mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
                <Link to="/edition" className="hover:text-red-600 transition-colors">
                  La Edición
                </Link>
              </h2>
            </div>
            <div className="grid grid-cols-12 gap-8">
              {/* Magazine Cover */}
              <div className="col-span-12 md:col-span-4">
                <Link to={`/edition/${editions[0].edition_id}`} className="block group">
                  <div className="relative aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden shadow-lg">
                    {editions[0].cover_image ? (
                      <img 
                        src={getImageUrl(editions[0], 0)}
                        alt={editions[0].title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-6 text-white">
                        <p className="text-xs uppercase tracking-widest mb-4">Edición Semanal</p>
                        <h3 className="text-xl font-bold text-center" style={{ fontFamily: SERIF_FONT }}>
                          {editions[0].title}
                        </h3>
                        <p className="text-sm mt-4 opacity-70">Semana {editions[0].week_number}</p>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
              
              {/* Edition Articles */}
              <div className="col-span-12 md:col-span-8">
                <div className="grid grid-cols-2 gap-6">
                  {editions[0].sections?.slice(0, 4).map((section, i) => (
                    <div key={i} className="group">
                      <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-1">
                        {section.section_type?.replace('_', ' ')}
                      </p>
                      <h4 
                        className="font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2"
                        style={{ fontFamily: SERIF_FONT }}
                      >
                        {section.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">{section.author}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* DESKTOP: Más Noticias */}
        {moreArticles.length > 0 && (
          <section className="hidden lg:block mt-16 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Más de Influencia & Poder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {moreArticles.slice(0, 8).map((article, i) => (
                <Link 
                  key={article.dossier_id}
                  to={`/dossiers/${article.dossier_id}`}
                  className="block group"
                >
                  <div className="relative aspect-[16/10] mb-3 overflow-hidden bg-gray-100 rounded">
                    <img 
                      src={getImageUrl(article, i)}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {article.sector}
                  </p>
                  <h3 
                    className="font-bold text-gray-900 group-hover:text-red-600 transition-colors leading-snug line-clamp-2"
                    style={{ fontFamily: SERIF_FONT }}
                  >
                    {article.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* NEWSLETTER SECTION - Mobile optimized */}
      <section className="bg-gray-900 text-white py-8 lg:py-12 mt-8 lg:mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-xl lg:text-2xl font-bold mb-2" style={{ fontFamily: SERIF_FONT }}>
            Suscríbete al Newsletter
          </h2>
          <p className="text-gray-400 text-sm mb-4 lg:mb-6">Recibe las noticias más importantes directo en tu correo</p>
          
          {/* Mobile: Stacked layout */}
          <div className="flex flex-col sm:flex-row max-w-md mx-auto gap-2">
            <input 
              type="email" 
              placeholder="Tu correo electrónico"
              className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 min-h-[48px]"
            />
            {/* Fitts Law: Large button */}
            <button className="px-6 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-lg font-bold transition-colors min-h-[48px]">
              Suscribirse
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
